import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 30;
const MAX_BYTES = 15 * 1024 * 1024;
export const absUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

/** Multiple PNG/JPG upload with thumbnails, reorder and remove. value = [{url, filename, size}] */
export default function MultiImageUpload({ value = [], onChange, testId = "multi-image" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(null);

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_FILES - value.length;
    if (picked.length > room) toast.error(`Maximum ${MAX_FILES} images — ${picked.length - Math.max(room, 0)} ignorée(s)`);
    const files = picked.slice(0, Math.max(room, 0));
    let next = [...value];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!/^image\/(png|jpe?g)$/i.test(f.type)) { toast.error(`${f.name} : seuls PNG et JPG sont acceptés`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name} : trop lourd (${(f.size / 1048576).toFixed(1)} Mo, max 15 Mo)`); continue; }
      setBusy({ done: i, total: files.length });
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { timeout: 120000 });
        next = [...next, { url: data.url, filename: f.name, size: f.size }];
        onChange(next);
      } catch (err) {
        toast.error(`${f.name} : ${formatApiErrorDetail(err.response?.data?.detail) || "échec de l'upload"}`);
      }
    }
    setBusy(null);
  };

  const move = (i, d) => {
    const n = [...value];
    [n[i], n[i + d]] = [n[i + d], n[i]];
    onChange(n);
  };

  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
        {value.map((img, i) => (
          <div key={img.url} className="group relative aspect-square overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50" data-testid={`${testId}-item-${i}`}>
            <img src={absUrl(img.url)} alt={img.filename} className="h-full w-full object-cover" />
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-rose-600 shadow hover:bg-white" title="Retirer" data-testid={`${testId}-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
            <div className="absolute inset-x-1 bottom-1 flex justify-between opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="grid h-6 w-6 place-items-center rounded-full bg-white/90 shadow disabled:invisible" data-testid={`${testId}-left-${i}`}><ChevronLeft className="h-3.5 w-3.5" /></button>
              <button type="button" disabled={i === value.length - 1} onClick={() => move(i, 1)} className="grid h-6 w-6 place-items-center rounded-full bg-white/90 shadow disabled:invisible" data-testid={`${testId}-right-${i}`}><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {value.length < MAX_FILES && (
          <button type="button" disabled={!!busy} onClick={() => ref.current?.click()} className="grid aspect-square place-items-center rounded-lg border-2 border-dashed border-neutral-300 text-neutral-500 transition-colors hover:border-wine-400 hover:text-wine-600 disabled:opacity-60" data-testid={`${testId}-add`}>
            {busy ? <span className="text-center text-[10px]"><Loader2 className="mx-auto h-5 w-5 animate-spin" />{busy.done + 1}/{busy.total}</span> : <span className="text-center text-[10px] font-semibold"><ImagePlus className="mx-auto h-5 w-5" />Ajouter</span>}
          </button>
        )}
      </div>
      <p className="mt-1 text-[10px] text-neutral-400">PNG ou JPG · 15 Mo max par image · {value.length}/{MAX_FILES} images · sélection multiple possible</p>
      <input ref={ref} type="file" multiple accept="image/png,image/jpeg" className="hidden" onChange={onPick} data-testid={`${testId}-input`} />
    </div>
  );
}
